import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import {
  CalendarBlank, Clock, ArrowRight, ArrowLeft, MapPin, CheckCircle,
  GoogleLogo, EnvelopeSimple, Phone, User as UserIcon, ChatCircleText, Globe,
} from '@phosphor-icons/react';
import { publicBookingApi } from '../lib/bookingApi';
import TimezoneSelect from '../components/TimezoneSelect';
import { detectBrowserTimezone, timeLabelInTz, formatTimezoneLabel } from '../lib/tz';

// ---- helpers ----
const pad = (n) => String(n).padStart(2, '0');
const toDayIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sameDay = (a, b) => toDayIso(a) === toDayIso(b);
const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

function PublicShell({ children }) {
  const isEmbed = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('embed') === '1';
  if (isEmbed) {
    return (
      <div className="min-h-screen bg-background" data-testid="public-booking-shell-embed">
        <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-4 sm:py-6">{children}</main>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#f5f6fa]" data-testid="public-booking-shell">
      <header className="w-full border-b border-border bg-background">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/login" className="flex items-center gap-3">
            <div
              className="h-11 w-12 flex items-center justify-center text-white font-heading font-bold text-base tracking-tight"
              style={{ background: '#002677' }}
            >
              BIL
            </div>
            <div className="hidden sm:block leading-tight">
              <p className="text-sm font-semibold">Schedule &amp; Remind</p>
              <p className="text-xs text-muted-foreground">Free booking pages by BIL</p>
            </div>
          </Link>
          <Link
            to="/register"
            className="text-sm font-medium text-[#002677] hover:underline"
          >
            Get your own free booking page →
          </Link>
        </div>
      </header>
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 sm:py-12">{children}</main>
    </div>
  );
}

// ====================================================================
// /book/:slug — profile + list of event types
// ====================================================================

export function PublicBookingProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await publicBookingApi.profile(slug);
        setData(d);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Profile not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <PublicShell><div className="text-sm text-muted-foreground">Loading…</div></PublicShell>;
  if (!data) {
    return (
      <PublicShell>
        <Card className="rounded-sm p-10 text-center border-dashed">
          <h2 className="font-heading text-xl font-semibold">No booking page here</h2>
          <p className="text-sm text-muted-foreground mt-2">This URL doesn&apos;t exist.</p>
        </Card>
      </PublicShell>
    );
  }
  const { profile, event_types: types } = data;
  return (
    <PublicShell>
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Book a time with</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-1">{profile.name}</h1>
        {profile.welcome && (
          <p className="text-base text-muted-foreground mt-4 max-w-2xl">{profile.welcome}</p>
        )}
      </div>
      {types.length === 0 ? (
        <Card className="rounded-sm p-10 text-center border-dashed">
          <p className="text-sm text-muted-foreground">No event types available yet. Check back soon!</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {types.map((et) => (
            <button
              key={et.id}
              type="button"
              onClick={() => navigate(`/book/${slug}/${et.slug}`)}
              className="group text-left bg-background border border-border rounded-sm p-5 hover:border-[#002677] hover:shadow-md transition-all"
              style={{ borderLeft: `4px solid ${et.color || '#002677'}` }}
              data-testid={`public-event-type-${et.slug}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-base">{et.title}</h3>
                  {et.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{et.description}</p>
                  )}
                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {et.duration_minutes} min</span>
                    {et.location && (
                      <span className="inline-flex items-center gap-1"><MapPin size={12} /> {et.location}</span>
                    )}
                  </div>
                </div>
                <ArrowRight
                  size={18}
                  weight="bold"
                  className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#002677] mt-1"
                />
              </div>
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-10 text-center">
        Powered by <a className="underline hover:text-[#002677]" href="/">BIL · Schedule &amp; Remind</a>
      </p>
    </PublicShell>
  );
}

// ====================================================================
// /book/:slug/:eventSlug — date + slot picker + form
// ====================================================================

export function PublicBookingEventPage() {
  const { slug, eventSlug } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(today));
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookerTz, setBookerTz] = useState(() => detectBrowserTimezone());
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await publicBookingApi.eventType(slug, eventSlug);
        setMeta(d);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Event type not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, eventSlug]);

  const loadSlots = useCallback(async (d) => {
    setSlotsLoading(true);
    try {
      const { data: r } = await publicBookingApi.slots(slug, eventSlug, toDayIso(d));
      setSlots(r.slots || []);
    } catch (_) {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [slug, eventSlug]);

  useEffect(() => {
    if (selectedDate) {
      loadSlots(selectedDate);
      setSelectedSlot(null);
    }
  }, [selectedDate, loadSlots]);

  const submit = async () => {
    if (!selectedSlot) return;
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const { data: r } = await publicBookingApi.book(slug, eventSlug, {
        start_at: selectedSlot,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
        notes: form.notes.trim(),
      });
      setConfirmed(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <PublicShell><div className="text-sm text-muted-foreground">Loading…</div></PublicShell>;
  if (!meta) return <PublicShell><Card className="rounded-sm p-10 text-center border-dashed"><p>Event type not found.</p></Card></PublicShell>;

  if (confirmed) {
    return (
      <PublicShell>
        <Card className="rounded-sm p-10 text-center border-dashed" data-testid="public-booking-success">
          <CheckCircle size={48} weight="fill" className="mx-auto text-[#00B140]" />
          <h2 className="font-heading text-2xl font-bold mt-4">Confirmed!</h2>
          <p className="text-sm text-muted-foreground mt-2">
            You&apos;re booked with <strong>{meta.profile.name}</strong> on{' '}
            <strong>{new Date(confirmed.start_at).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: bookerTz })}</strong>{' '}
            ({formatTimezoneLabel(bookerTz)}).
          </p>
          <p className="text-sm text-muted-foreground mt-1">A confirmation has been sent to <strong>{confirmed.customer_email}</strong>.</p>
          {confirmed.google_event_id && (
            <p className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1">
              <GoogleLogo size={12} weight="fill" /> Added to {meta.profile.name}&apos;s Google Calendar (you should receive a Google invite too).
            </p>
          )}
        </Card>
      </PublicShell>
    );
  }

  const { profile, event_type: et } = meta;
  return (
    <PublicShell>
      <Link
        to={`/book/${slug}`}
        className="text-sm text-muted-foreground hover:text-[#002677] inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={14} /> All event types
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: event type info */}
        <div className="lg:col-span-4">
          <div className="bg-background border border-border rounded-sm p-5"
               style={{ borderLeft: `4px solid ${et.color || '#002677'}` }}>
            <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">{profile.name}</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight mt-1">{et.title}</h1>
            {et.description && <p className="text-sm text-muted-foreground mt-3">{et.description}</p>}
            <div className="mt-4 space-y-2 text-sm">
              <div className="inline-flex items-center gap-2"><Clock size={14} className="text-[#002677]" /> {et.duration_minutes} min</div>
              {et.location && (
                <div className="flex items-start gap-2"><MapPin size={14} className="text-[#002677] mt-0.5" /><span>{et.location}</span></div>
              )}
              <div className="flex items-start gap-2"><Globe size={14} className="text-[#002677] mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">Times shown in</p>
                  <TimezoneSelect
                    value={bookerTz}
                    onChange={setBookerTz}
                    testId="public-tz"
                    className="w-full max-w-[220px]"
                  />
                  {bookerTz !== profile.timezone && (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {profile.name}&apos;s timezone: {formatTimezoneLabel(profile.timezone)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: calendar + slots */}
        <div className="lg:col-span-8">
          <div className="bg-background border border-border rounded-sm p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalendarPanel
                today={today}
                monthCursor={monthCursor}
                setMonthCursor={setMonthCursor}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
              <SlotPanel
                selectedDate={selectedDate}
                slots={slots}
                slotsLoading={slotsLoading}
                selectedSlot={selectedSlot}
                setSelectedSlot={setSelectedSlot}
                timezone={bookerTz}
              />
            </div>
            {selectedSlot && (
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="font-heading font-semibold text-base mb-4">Your details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldIcon icon={UserIcon} label="Name *">
                    <Input data-testid="public-name" value={form.customer_name}
                           onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="rounded-sm" />
                  </FieldIcon>
                  <FieldIcon icon={EnvelopeSimple} label="Email *">
                    <Input type="email" data-testid="public-email" value={form.customer_email}
                           onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="rounded-sm" />
                  </FieldIcon>
                </div>
                <div className="mt-4">
                  <FieldIcon icon={Phone} label="Phone (optional)">
                    <Input data-testid="public-phone" value={form.customer_phone}
                           onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                           placeholder="+1 415 555 0123" className="rounded-sm" />
                  </FieldIcon>
                </div>
                <div className="mt-4">
                  <FieldIcon icon={ChatCircleText} label="Anything to share before the meeting?">
                    <Textarea rows={3} data-testid="public-notes" value={form.notes}
                              onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" />
                  </FieldIcon>
                </div>
                <Button onClick={submit} disabled={submitting}
                        className="rounded-sm bg-[#002677] hover:bg-[#002677]/90 mt-5 w-full sm:w-auto"
                        data-testid="public-submit">
                  {submitting ? 'Booking…' : `Confirm booking · ${new Date(selectedSlot).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: bookerTz })}`}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-10 text-center">
        Powered by <a className="underline hover:text-[#002677]" href="/">BIL · Schedule &amp; Remind</a>
      </p>
    </PublicShell>
  );
}

function FieldIcon({ icon: Icon, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold inline-flex items-center gap-1.5">
        <Icon size={12} /> {label}
      </Label>
      {children}
    </div>
  );
}

function CalendarPanel({ today, monthCursor, setMonthCursor, selectedDate, setSelectedDate }) {
  const monthLabel = monthCursor.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  const firstDay = (startOfMonth(monthCursor).getDay() + 6) % 7; // Mon=0
  const daysInMonth = endOfMonth(monthCursor).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonthCursor(addDays(startOfMonth(monthCursor), -1))}
                className="h-8 w-8 inline-flex items-center justify-center rounded-sm hover:bg-accent/40 text-muted-foreground"
                data-testid="public-cal-prev">
          <ArrowLeft size={16} />
        </button>
        <span className="font-heading text-sm font-semibold uppercase tracking-wider">{monthLabel}</span>
        <button onClick={() => setMonthCursor(addDays(endOfMonth(monthCursor), 1))}
                className="h-8 w-8 inline-flex items-center justify-center rounded-sm hover:bg-accent/40 text-muted-foreground"
                data-testid="public-cal-next">
          <ArrowRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((w) => <span key={w}>{w}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} className="h-9" />;
          const date = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), d);
          const past = date < today;
          const isToday = sameDay(date, today);
          const isSelected = selectedDate && sameDay(date, selectedDate);
          return (
            <button
              key={d}
              type="button"
              disabled={past}
              onClick={() => setSelectedDate(date)}
              data-testid={`public-cal-day-${toDayIso(date)}`}
              className={`h-9 rounded-sm text-sm font-medium transition-colors ${
                past ? 'text-muted-foreground/40 cursor-not-allowed' :
                isSelected ? 'bg-[#002677] text-white' :
                isToday ? 'border border-[#002677] text-[#002677]' :
                'hover:bg-[#002677]/10 text-foreground'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SlotPanel({ selectedDate, slots, slotsLoading, selectedSlot, setSelectedSlot, timezone }) {
  if (!selectedDate) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground text-center p-4">
        Pick a date to see available times
      </div>
    );
  }
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">
        {selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
      </p>
      {slotsLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : slots.length === 0 ? (
        <p className="text-sm text-muted-foreground">No availability on this day.</p>
      ) : (
        <div className="max-h-[340px] overflow-y-auto pr-1 space-y-1.5" data-testid="public-slots">
          {slots.map((iso) => {
            const active = selectedSlot === iso;
            const label = timeLabelInTz(new Date(iso), timezone);
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setSelectedSlot(iso)}
                data-testid={`public-slot-${iso}`}
                className={`w-full text-center py-2 rounded-sm text-sm font-medium border transition-colors ${
                  active
                    ? 'bg-[#002677] border-[#002677] text-white'
                    : 'border-[#002677]/30 text-[#002677] hover:border-[#002677]'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-2">{formatTimezoneLabel(timezone)}</p>
    </div>
  );
}
